import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";

import { IMessageMetadata } from "../../../../types/chat";
import CoreExpander from "../../../ui/core/CoreExpander";
import ChatMarkdownDataTable, {
  MarkdownDataTableColumn,
} from "./ChatMarkdownDataTable";

const EDGE_FADE_WIDTH = 40;

/**
 * Wraps a table and overlays a left/right edge fade whenever the table's horizontal
 * scroll container (the `.overflow-x-auto` div from MarkdownTable) has more content to
 * scroll to, mirroring the scroll-fade pattern used elsewhere in chat (see ChatSection's
 * top/bottom fade).
 */
const TableScrollFade: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const updateFade = (el: HTMLElement) => {
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftFade(scrollLeft > 0);
    setShowRightFade(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    const scrollEl =
      containerRef.current?.querySelector<HTMLElement>(".overflow-x-auto");
    if (!scrollEl) return;

    updateFade(scrollEl);

    if (typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(() => updateFade(scrollEl));
    resizeObserver.observe(scrollEl);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative min-w-0"
      onScrollCapture={(e) => updateFade(e.target as HTMLElement)}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 transition-opacity duration-150"
        style={{
          width: EDGE_FADE_WIDTH,
          opacity: showLeftFade ? 1 : 0,
          background:
            "linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))",
        }}
      />
      {children}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 transition-opacity duration-150"
        style={{
          width: EDGE_FADE_WIDTH,
          opacity: showRightFade ? 1 : 0,
          background:
            "linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))",
        }}
      />
    </div>
  );
};

const ExpandTableIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_2666_3507"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="16"
      height="16"
    >
      <rect width="16" height="16" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_2666_3507)">
      <path
        d="M11.3664 9.76654H9.79977C9.67677 9.76654 9.57383 9.8077 9.49094 9.89004C9.40794 9.97226 9.36644 10.0744 9.36644 10.1964C9.36644 10.3184 9.40794 10.4216 9.49094 10.5062C9.57383 10.5909 9.67677 10.6332 9.79977 10.6332H11.6973C11.8502 10.6332 11.9777 10.5821 12.0798 10.4799C12.182 10.3778 12.2331 10.2503 12.2331 10.0974V8.19987C12.2331 8.07687 12.1919 7.97393 12.1096 7.89104C12.0274 7.80804 11.9253 7.76654 11.8033 7.76654C11.6813 7.76654 11.578 7.80804 11.4934 7.89104C11.4088 7.97393 11.3664 8.07687 11.3664 8.19987V9.76654ZM4.63311 6.2332H6.19977C6.32277 6.2332 6.42572 6.19204 6.50861 6.1097C6.59161 6.02748 6.63311 5.92537 6.63311 5.80337C6.63311 5.68137 6.59161 5.57809 6.50861 5.49354C6.42572 5.40887 6.32277 5.36654 6.19977 5.36654H4.30227C4.14938 5.36654 4.02188 5.41765 3.91977 5.51987C3.81755 5.62198 3.76644 5.74948 3.76644 5.90237V7.79987C3.76644 7.92287 3.80761 8.02581 3.88994 8.1087C3.97216 8.1917 4.07427 8.2332 4.19627 8.2332C4.31827 8.2332 4.42155 8.1917 4.50611 8.1087C4.59077 8.02581 4.63311 7.92287 4.63311 7.79987V6.2332ZM3.00494 12.4665C2.70483 12.4665 2.45116 12.3629 2.24394 12.1555C2.03672 11.9482 1.93311 11.6944 1.93311 11.394V4.60037C1.93311 4.30004 2.03672 4.04709 2.24394 3.84154C2.45116 3.63598 2.70483 3.5332 3.00494 3.5332H12.9946C13.2947 3.5332 13.5484 3.63687 13.7556 3.8442C13.9628 4.05154 14.0664 4.30537 14.0664 4.6057V11.3994C14.0664 11.6997 13.9628 11.9526 13.7556 12.1582C13.5484 12.3638 13.2947 12.4665 12.9946 12.4665H3.00494ZM3.00494 11.5999H12.9946C13.0459 11.5999 13.0929 11.5785 13.1356 11.5357C13.1784 11.493 13.1998 11.446 13.1998 11.3947V4.60504C13.1998 4.5537 13.1784 4.5067 13.1356 4.46404C13.0929 4.42126 13.0459 4.39987 12.9946 4.39987H3.00494C2.95361 4.39987 2.90661 4.42126 2.86394 4.46404C2.82116 4.5067 2.79977 4.5537 2.79977 4.60504V11.3947C2.79977 11.446 2.82116 11.493 2.86394 11.5357C2.90661 11.5785 2.95361 11.5999 3.00494 11.5999Z"
        fill="#6B7280"
      />
    </g>
  </svg>
);

const CloseIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    aria-hidden
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export interface ChatMessageExpandableTableProps {
  title: string;
  defaultExpanded: boolean;
  columns: MarkdownDataTableColumn[];
  data: Record<string, unknown>[];
  rowKeyField?: string;
  metadata?: IMessageMetadata;
}

/** Table section with an "Expand Table" action that opens the same table full-size in a modal. */
const ChatMessageExpandableTable: React.FC<ChatMessageExpandableTableProps> = ({
  title,
  defaultExpanded,
  columns,
  data,
  rowKeyField,
  metadata,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <CoreExpander
        title={title}
        defaultExpanded={defaultExpanded}
        headerAction={
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            className="flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 font-manrope text-xs font-semibold text-chat-dark hover:bg-gray-50"
          >
            Expand Table
            <ExpandTableIcon />
          </button>
        }
      >
        <TableScrollFade>
          <ChatMarkdownDataTable
            columns={columns}
            data={data}
            rowKeyField={rowKeyField}
            metadata={metadata}
          />
        </TableScrollFade>
      </CoreExpander>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            ...(window && window?.innerWidth
              ? { maxWidth: window.innerWidth }
              : {}),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {title}
          <IconButton
            onClick={() => setIsModalOpen(false)}
            size="small"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowX: "auto" }}>
          <TableScrollFade>
            <ChatMarkdownDataTable
              columns={columns}
              data={data}
              rowKeyField={rowKeyField}
              metadata={metadata}
            />
          </TableScrollFade>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatMessageExpandableTable;
